if (!customElements.get('sticky-buy-bar')) {
  customElements.define(
    'sticky-buy-bar',
    class StickyBuyBar extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.sectionId = this.dataset.sectionId;
        this.priceTarget = this.querySelector('[data-sticky-price]');
        this.savingTarget = this.querySelector('[data-sticky-saving]');
        this.button = this.querySelector('[data-sticky-buy-button]');
        this.buttonText = this.querySelector('[data-sticky-buy-button-text]');
        this.spinner = this.querySelector('.loading__spinner');
        this.buttonLabel = this.buttonText.textContent.trim();
        this.soldOutText = this.dataset.soldOutText;
        this.variantData = this.getVariantData();

        this.button.addEventListener('click', this.onBuyClick.bind(this));
        this.watchLoadingState();

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId !== this.sectionId) return;
          this.updateFromVariant(event.data.variant);
        });

        this.watchMainButton();
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
        if (this.observer) this.observer.disconnect();
        if (this.loadingObserver) this.loadingObserver.disconnect();
      }

      // product-form.js drives the loading state on the real add to cart button.
      // Mirror it here so the sticky button shows the same spinner for the same duration.
      watchLoadingState() {
        const target = this.mainButton;
        if (!target) return;

        this.loadingObserver = new MutationObserver(() => this.setLoading(target.classList.contains('loading')));

        this.loadingObserver.observe(target, { attributes: true, attributeFilter: ['class'] });
      }

      setLoading(isLoading) {
        this.button.classList.toggle('loading', isLoading);
        this.spinner.classList.toggle('hidden', !isLoading);

        if (isLoading) {
          this.button.setAttribute('aria-disabled', 'true');
        } else {
          this.button.removeAttribute('aria-disabled');
        }
      }

      getVariantData() {
        try {
          return JSON.parse(this.querySelector('.sticky-buy-bar__variant-data').textContent);
        } catch (error) {
          return {};
        }
      }

      get mainButton() {
        return document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      }

      // Reveal the bar only once the real add-to-cart button has scrolled out of view,
      // so the two CTAs are never on screen at the same time.
      watchMainButton() {
        const target = this.mainButton;
        if (!target) {
          this.hidden = false;
          return;
        }

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.hidden = entry.isIntersecting;
              document.body.classList.toggle('sticky-buy-bar-visible', !entry.isIntersecting);
            });
          },
          { rootMargin: '0px 0px -80px 0px' }
        );

        this.observer.observe(target);
      }

      updateFromVariant(variant) {
        if (!variant) {
          this.setUnavailable();
          return;
        }

        const data = this.variantData[variant.id];
        if (!data) return;

        this.priceTarget.textContent = data.price;

        if (data.saving) {
          this.savingTarget.textContent = `${this.savingTarget.dataset.savingLabel} ${data.saving}`;
          this.savingTarget.hidden = false;
        } else {
          this.savingTarget.hidden = true;
        }

        if (data.available) {
          this.button.disabled = false;
          this.buttonText.textContent = this.buttonLabel;
        } else {
          this.setUnavailable();
        }
      }

      setUnavailable() {
        this.button.disabled = true;
        this.buttonText.textContent = this.soldOutText;
      }

      // Delegate to the real add-to-cart button so variant state, quantity rules,
      // error handling and the cart drawer all keep working as they already do.
      onBuyClick() {
        if (this.button.getAttribute('aria-disabled') === 'true') return;

        const target = this.mainButton;
        if (!target) return;

        target.click();
      }
    }
  );
}
